import { createFileRoute, redirect, Link, Outlet, useRouterState, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useArticleCollection } from '@/queries/article-collections';
import { useUserTier } from '@/queries/subscriptions';
import { CollectionContents } from '@/components/CollectionContents';
import { CircularLoader } from '@/components/CircularLoader';
import styles from './collectionDetail.module.scss';

export const Route = createFileRoute('/articles/collections/$collectionId')({
  component: CollectionDetailPage,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function CollectionDetailPage() {
  const { collectionId } = Route.useParams();
  const routerState = useRouterState();
  const navigate = useNavigate();
  const userTier = useUserTier();
  const { data: collection, isLoading, error } = useArticleCollection(collectionId);

  // Redirect foundation tier users to articles page
  useEffect(() => {
    if (userTier === 'foundation') {
      navigate({
        to: '/articles',
        replace: true,
      });
    }
  }, [userTier, navigate]);

  // Check if we're on a child route (reading mode: /articles/collections/:id/articles/:articleId)
  // The child route has /articles/ appearing after the /collections/:id segment
  const isChildRoute = /\/articles\/collections\/[^/]+\/articles\//.test(routerState.location.pathname);

  // Show loading while redirecting foundation users
  if (userTier === 'foundation') {
    return (
      <div className={styles.collectionDetail__loading}>
        <CircularLoader />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.collectionDetail__loading}>
        <CircularLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.collectionDetail__error}>
        <p>Error loading collection: {error.message}</p>
        <Link to="/articles/collections">Return to Collections</Link>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className={styles.collectionDetail__notFound}>
        <p>Collection not found</p>
        <Link to="/articles/collections">Return to Collections</Link>
      </div>
    );
  }

  // If we're on a child route (reading mode), render the child route component
  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <div className={styles.collectionDetail}>
      <CollectionContents collection={collection} />
    </div>
  );
}
